'use client';

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { Check, Minus, ArrowRight, Loader2 } from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { useUser } from '@clerk/nextjs'
import { PRICING_CONFIG } from '@/lib/pricing-config'
import { motion } from 'framer-motion'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'

const PRICING_PLANS = [
  {
    name: 'Starter',
    priceId: 'free',
    price: 'Free',
    type: 'Month',
    cta: 'Start for Free',
    href: '/auth/sign-up',
    highlight: false,
    description: 'Perfect for creators just getting started.'
  },
  {
    name: 'Pro',
    priceId: 'pro',
    type: 'Month',
    cta: 'Upgrade to Pro',
    href: '/dashboard', 
    highlight: true,
    description: 'Maximum growth for independent brands.'
  },
  {
    name: 'Premium',
    priceId: 'premium',
    type: 'Month',
    cta: 'Upgrade to Premium',
    href: '/dashboard',
    highlight: false,
    description: 'Ultimate access for agencies & enterprises.'
  }
];

const PLAN_FEATURES = [
  {
    category: 'Core Link & Engagement Management',
    features: [
      { name: 'Shortened Links', starter: '50 Total', pro: '50 / Month', premium: 'Unlimited' },
      { name: 'QR Codes', starter: '20 Total', pro: '50 / Month', premium: 'Unlimited' },
      { name: 'Custom Domains', starter: false, pro: false, premium: true },
      { name: 'Link in Bio Pages', starter: '1 Total', pro: '5 Total', premium: 'Unlimited' },
    ]
  },
  {
    category: 'Monetization & Lead Gen',
    features: [
      { name: 'Product Stores', starter: '1 Total', pro: '2 Total', premium: '5 Total' },
      { name: 'Products per Store', starter: '2 Products', pro: '10 / Month', premium: 'Unlimited' },
      { name: 'Forms & Polls', starter: '5 Total', pro: '10 / Month', premium: 'Unlimited' },
      { name: 'AI Form/Poll Generation', starter: false, pro: '5 / Month', premium: '20 / Month' },
      { name: 'Headless Form Endpoints', starter: '2 Total', pro: '10 / Month', premium: 'Unlimited' },
      { name: 'Referral Trackers', starter: '5 Total', pro: '10 / Month', premium: 'Unlimited' }
    ]
  },
  {
    category: 'Analytics & Tracking',
    features: [
      { name: 'Basic Engagement Metrics', starter: true, pro: true, premium: true },
      { name: 'Analytics Log History', starter: '7 Days', pro: 'Unlimited', premium: 'Unlimited' },
      { name: 'Fraud & Bot Detection', starter: true, pro: true, premium: true },
      { name: 'Teams & Collaboration', starter: '1 Team', pro: '3 Teams', premium: '5 Teams' },
      { name: 'Team Members', starter: 'Up to 2', pro: 'Up to 4', premium: 'Up to 10' },
      { name: 'Developer API', starter: '1 API Key', pro: '10 API Keys', premium: '100 API Keys' }
    ]
  }
];

export default function PricingClient() {
  const { isSignedIn: session } = useUser();
  const [currency, setCurrency] = useState<'USD' | 'INR'>('USD');
  const [billingInterval, setBillingInterval] = useState<'monthly' | '10m'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);
  const router = useRouter();

  // Fetch current user plan
  useEffect(() => {
    if (session) {
      fetch('/api/usage')
        .then(r => r.json())
        .then(data => {
          if (data.plan) setUserPlan(data.plan);
          if (data.planExpiresAt) setPlanExpiresAt(data.planExpiresAt);
        })
        .catch(() => {});
    }
  }, [session]);

  useEffect(() => {
    // Detect South Asia from timezone (most reliable client-side signal)
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const lang = navigator.language || '';
      
      const saTimezones = ['Asia/Kolkata', 'Asia/Calcutta', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Kathmandu', 'Asia/Colombo', 'Asia/Thimphu', 'Asia/Maldives'];
      const saLangPrefixes = ['hi', 'bn', 'te', 'mr', 'ta', 'gu', 'kn', 'ml', 'pa', 'ur'];
      
      const isTimezoneMatch = saTimezones.some(z => tz.includes(z));
      const isLangMatch = lang.includes('-IN') || lang.includes('-PK') || lang.includes('-BD') || 
                          saLangPrefixes.some(p => lang.startsWith(p));
      
      if (isTimezoneMatch || isLangMatch) {
        setCurrency('INR');
      }
    } catch { }

    // IP geolocation as backup (only upgrades to INR, never downgrades)
    fetch('/api/country-code')
      .then(res => res.ok ? res.text() : Promise.reject())
      .then(code => {
        const saCountries = ['IN', 'PK', 'BD', 'LK', 'NP', 'BT', 'MV'];
        if (saCountries.includes(code.trim())) {
          setCurrency('INR');
        }
      })
      .catch(() => {});
  }, []);

  const getPrice = (plan: typeof PRICING_PLANS[0]) => {
    if (plan.name === 'Starter') return 'Free';
    const is10M = billingInterval === '10m';
    const interval = is10M ? '10m' : 'monthly';
    const pId = plan.priceId as 'pro' | 'premium';
    
    if (currency === 'INR') {
      return `₹${PRICING_CONFIG[pId].INR[interval]}`;
    }
    return `$${PRICING_CONFIG[pId].USD[interval]}`;
  };

  const getBilledDetails = (plan: typeof PRICING_PLANS[0]) => {
    if (plan.name === 'Starter') return null;
    if (billingInterval === 'monthly') return null;
    const pId = plan.priceId as 'pro' | 'premium';
    
    if (currency === 'INR') {
      return `Billed ₹${PRICING_CONFIG[pId].INR.billed10m} / 10 months`;
    }
    return `Billed $${PRICING_CONFIG[pId].USD.billed10m} / 10 months`;
  };

  const handleUpgrade = async (planId: string) => {
    if (!session) {
      router.push('/auth/sign-up');
      return;
    }

    try {
      setLoadingPlan(planId);
      
      const response = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, isSouthAsia: currency === 'INR', billingInterval }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Checkout API error:', { status: response.status, ...errorData });
        
        if (response.status === 401) {
          toast.error('Refreshing your session... Please try again.', { id: 'dodo-checkout' });
          setTimeout(() => window.location.reload(), 1500);
          setLoadingPlan(null);
          return;
        }
        throw new Error(errorData.detail || errorData.error || 'Failed to initiate checkout session');
      }

      const data = await response.json();

      if (!data.checkoutUrl) {
        throw new Error('Could not retrieve checkout URL from the payment gateway.');
      }

      sessionStorage.setItem('pending_plan_id', planId);
      sessionStorage.setItem('pending_billing_interval', billingInterval);

      toast.loading('Redirecting to secure checkout...', { id: 'dodo-checkout' });
      
      window.location.href = data.checkoutUrl;

    } catch (err: any) {
      console.error('Checkout error:', err);
      toast.error(err.message || 'Failed to initiate payment. Please try again.', { id: 'dodo-checkout' });
      setLoadingPlan(null);
    }
  };

  const renderPlanCta = (plan: typeof PRICING_PLANS[0], suffix = '') => {
    const isStarter = plan.name === 'Starter';
    const isUpgrading = loadingPlan === plan.priceId;
    const isCurrentPlan = (isStarter && (!userPlan || userPlan === 'free' || userPlan === 'starter')) ||
                          (!isStarter && userPlan === plan.priceId);

    if (isCurrentPlan && session) {
      return (
        <div className="w-full space-y-2">
          <div className="w-full h-12 flex items-center justify-center rounded-xl font-bold text-sm border-2 border-primary/50 bg-primary/10 text-primary">
            <Check className="w-4 h-4 mr-2" />
            Your Current Plan
          </div>
          {planExpiresAt && !isStarter && (
            <p className="text-center text-white/40 text-xs">
              Active until {new Date(planExpiresAt).toLocaleDateString(undefined, { dateStyle: 'long' })}
            </p>
          )}
        </div>
      );
    }

    if (isStarter) {
      return (
        <Link
          id={`pricing-plan-${plan.priceId}-cta-${suffix}`}
          href={session ? '/dashboard' : plan.href}
          className="w-full h-12 flex items-center justify-center rounded-xl font-bold text-sm transition-all shadow-lg hover:-translate-y-1 bg-white text-black hover:bg-white/90"
        >
          {session ? 'Go to Dashboard' : plan.cta}
        </Link>
      );
    }

    return (
      <button
        id={`pricing-plan-${plan.priceId}-cta-${suffix}`}
        onClick={() => handleUpgrade(plan.priceId!)}
        disabled={loadingPlan !== null}
        className={`w-full h-12 flex items-center justify-center rounded-xl font-bold text-sm transition-all shadow-lg hover:-translate-y-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
          plan.highlight
            ? 'bg-primary text-white shadow-primary/20 hover:bg-primary/95'
            : 'bg-white text-black hover:bg-white/90'
        }`}
      >
        {isUpgrading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Processing...
          </span>
        ) : (
          plan.cta
        )}
      </button>
    );
  };

  return (
    <div className="relative min-h-screen bg-black overflow-hidden selection:bg-primary/30">
      <GeometricBackground />
      <MarketingNavbar />

      <main id="main-content">
        {/* Header */}
        <section className="relative pt-36 pb-12 z-10">
          <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter text-white uppercase mb-6">
                Compare <span className="text-primary">plans.</span>
              </h1>
              <p className="text-xl text-white/50 max-w-2xl mx-auto font-light">
                Clear limits, incredible value. Find the plan that fits your growth trajectory and scale up effortlessly as you expand.
              </p>

              {/* Billing Switcher */}
              <div className="flex items-center justify-center mt-6 gap-3 relative z-20">
                <span className={`text-sm font-semibold transition-colors duration-300 ${billingInterval === 'monthly' ? 'text-white font-bold' : 'text-white/40 font-normal'}`}>Monthly</span>
                <button
                  id="pricing-billing-toggle"
                  onClick={() => setBillingInterval(billingInterval === 'monthly' ? '10m' : 'monthly')}
                  className="relative w-14 h-7 bg-white/10 border border-white/15 rounded-full p-1 transition-colors hover:bg-white/15 cursor-pointer focus:outline-none flex items-center"
                >
                  <div
                    className={`w-5 h-5 bg-primary rounded-full transition-transform duration-300 shadow-[0_0_15px_rgba(0,102,255,0.8)] ${
                      billingInterval === '10m' ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-sm font-semibold transition-colors duration-300 ${billingInterval === '10m' ? 'text-white font-bold' : 'text-white/40 font-normal'} flex items-center gap-1.5`}>
                  Annual (10 Months)
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 rounded-md">Save 10%</span>
                </span>
              </div>

              {/* Currency Toggle */}
              <div className="flex items-center justify-center mt-4 gap-3 relative z-20">
                <span className={`text-sm font-semibold transition-colors duration-300 ${currency === 'USD' ? 'text-white font-bold' : 'text-white/40 font-normal'}`}>USD ($)</span>
                <button
                  id="pricing-currency-toggle"
                  onClick={() => setCurrency(currency === 'USD' ? 'INR' : 'USD')}
                  className="relative w-14 h-7 bg-white/10 border border-white/15 rounded-full p-1 transition-colors hover:bg-white/15 cursor-pointer focus:outline-none flex items-center"
                >
                  <div
                    className={`w-5 h-5 bg-amber-500 rounded-full transition-transform duration-300 shadow-[0_0_15px_rgba(245,158,11,0.8)] ${
                      currency === 'INR' ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-sm font-semibold transition-colors duration-300 ${currency === 'INR' ? 'text-white font-bold' : 'text-white/40 font-normal'}`}>INR (₹)</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Matrix */}
        <section className="relative pb-32 z-10">
          <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {/* Limits Legend */}
              <div className="mb-8 p-4 rounded-2xl bg-white/[0.02] border border-white/5 text-center text-xs sm:text-sm text-white/60">
                <span className="font-semibold text-primary">Note:</span> Total = lifetime cap, doesn&apos;t reset &middot; /Month = renews every cycle
              </div>

              {/* Mobile Card Layout */}
              <div className="lg:hidden space-y-6">
                {PRICING_PLANS.map((plan) => (
                  <div
                    key={plan.name}
                    className={`rounded-2xl border p-6 space-y-5 ${
                      plan.highlight
                        ? 'border-primary/40 bg-primary/[0.03]'
                        : 'border-white/[0.08] bg-white/[0.02]'
                    }`}
                  >
                    {plan.highlight && (
                      <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">Most Beneficial</span>
                    )}
                    <div>
                      <h3 className="text-2xl font-bold text-white uppercase">{plan.name}</h3>
                      <div className="flex flex-col mt-2">
                        <div className="flex items-end gap-1">
                          <span className="text-3xl font-bold text-white">{getPrice(plan)}</span>
                          {plan.name !== 'Starter' && <span className="text-white/40 mb-1 font-medium">/ {plan.type} (tax included)</span>}
                        </div>
                        {getBilledDetails(plan) && (
                          <span className="text-[10px] text-white/30 font-medium italic mt-1">{getBilledDetails(plan)}</span>
                        )}
                      </div>
                      <p className="text-sm text-white/50 font-light mt-2">{plan.description}</p>
                    </div>

                    <div className="space-y-2 pt-2">
                      {PLAN_FEATURES.map((category) => (
                        <div key={category.category}>
                          <p className="text-primary text-[10px] font-bold tracking-widest uppercase mb-2 mt-3">{category.category}</p>
                          {category.features.map((feature) => {
                            const val = plan.name === 'Starter' ? feature.starter : plan.name === 'Pro' ? feature.pro : feature.premium;
                            return (
                              <div key={feature.name} className="flex items-center justify-between py-1.5 border-b border-white/[0.04]">
                                <span className="text-xs text-white/60">{feature.name}</span>
                                <span className="text-xs text-white/80 font-semibold">
                                  {typeof val === 'boolean' ? (
                                    val ? (
                                      <>
                                        <Check className="w-4 h-4 text-green-500 inline" aria-hidden="true" />
                                        <span className="sr-only">Included</span>
                                      </>
                                    ) : (
                                      <>
                                        <Minus className="w-4 h-4 text-white/20 inline" aria-hidden="true" />
                                        <span className="sr-only">Not included</span>
                                      </>
                                    )
                                  ) : val}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {renderPlanCta(plan, 'mobile')}
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden lg:block w-full overflow-x-auto pb-8">
              <table className="w-full text-left border-collapse min-w-[800px]">
                
                {/* Table Header: Plan Cards */}
                <thead>
                  <tr>
                    <th className="p-6 border-b border-white/10 w-1/4"></th>
                    {PRICING_PLANS.map((plan) => (
                      <th key={plan.name} className="p-6 border-b border-white/10 w-1/4 align-top">
                        <div className="relative mb-2">
                           <h3 className="text-2xl font-bold text-white mb-1 uppercase">{plan.name}</h3>
                            <div className="flex flex-col mb-4">
                              <div className="flex items-end gap-1">
                                <span className="text-4xl font-bold text-white">{getPrice(plan)}</span>
                                {plan.name !== 'Starter' && <span className="text-white/40 mb-1 font-medium">/ {plan.type} (tax included)</span>}
                              </div>
                              {getBilledDetails(plan) && (
                                <span className="text-[10px] text-white/30 font-medium italic mt-1">{getBilledDetails(plan)}</span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 font-light mb-6 h-10">{plan.description}</p>
                           
                           {renderPlanCta(plan, 'desktop')}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Table Body: Features */}
                <tbody>
                  {PLAN_FEATURES.map((category, catIdx) => (
                    <React.Fragment key={category.category}>
                      {/* Category Title Row */}
                      <tr>
                        <td colSpan={4} className="py-6 px-6 bg-white/[0.02] border-b border-white/5">
                          <span className="text-primary font-bold tracking-widest uppercase text-sm">
                            {category.category}
                          </span>
                        </td>
                      </tr>
                      
                      {/* Feature Rows */}
                      {category.features.map((feature, featureIdx) => (
                        <tr key={feature.name} className="hover:bg-white/[0.01] transition-colors">
                          <td className="p-6 border-b border-white/5 text-white/80 font-medium">
                            {feature.name}
                          </td>
                          
                          {/* Starter Cell */}
                           <td className="p-6 border-b border-white/5 text-center">
                             {typeof feature.starter === 'boolean' ? (
                               feature.starter ? (
                                 <>
                                   <Check className="w-5 h-5 text-green-500 mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Starter: Included</span>
                                 </>
                               ) : (
                                 <>
                                   <Minus className="w-5 h-5 text-white/20 mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Starter: Not included</span>
                                 </>
                               )
                             ) : (
                               <span className="text-white/70 font-semibold">{feature.starter}</span>
                             )}
                           </td>

                          {/* Pro Cell */}
                           <td className="p-6 border-b border-white/5 text-center bg-primary/[0.02] border-x border-primary/10">
                             {typeof feature.pro === 'boolean' ? (
                               feature.pro ? (
                                 <>
                                   <Check className="w-5 h-5 text-primary mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Pro: Included</span>
                                 </>
                               ) : (
                                 <>
                                   <Minus className="w-5 h-5 text-white/20 mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Pro: Not included</span>
                                 </>
                               )
                             ) : (
                               <span className="text-white font-bold">{feature.pro}</span>
                             )}
                           </td>

                          {/* Premium Cell */}
                           <td className="p-6 border-b border-white/5 text-center">
                             {typeof feature.premium === 'boolean' ? (
                               feature.premium ? (
                                 <>
                                   <Check className="w-5 h-5 text-green-500 mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Premium: Included</span>
                                 </>
                               ) : (
                                 <>
                                   <Minus className="w-5 h-5 text-white/20 mx-auto" aria-hidden="true" />
                                   <span className="sr-only">Premium: Not included</span>
                                 </>
                               )
                             ) : (
                               <span className="text-white/70 font-semibold">{feature.premium}</span>
                             )}
                           </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>

              </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 border-t border-white/5 relative z-10 overflow-hidden">
           <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.04),transparent_70%)] blur-[100px] pointer-events-none" />
           <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white mb-6 uppercase">All set to upgrade?</h2>
              <p className="text-white/50 mb-2 text-lg">Your data is never locked, limits are clear, and scaling is instant.</p>
              <p className="text-sm text-primary font-medium tracking-wide mb-10 uppercase">No credit card, no lock-in</p>
              <Link
                 id="pricing-bottom-cta"
                 href={session ? '/dashboard' : '/auth/sign-up'}
                 className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white rounded-full font-bold hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(0,102,255,0.4)]"
              >
                 {session ? 'Go to Dashboard' : 'Start for Free'}
                 <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
           </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
